import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { Constants, I18n } from 'galarm-config'
import { Box, Card, Heading, Select, Spinner, Text } from 'grommet'
import { Refresh } from 'grommet-icons'
import React, { useEffect, useState } from 'react'
import { HorizontalLine } from 'web-components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import GlobalConfig from '../GlobalConfig'

// Show duration upto 14 days
const incidentsRetentionDurationOptions = [
  { label: I18n.t('noRetention'), value: 0 },
  { label: I18n.t('numDays', { count: 7 }), value: 7 * Constants.MSEC_IN_DAY },
  {
    label: I18n.t('numDays', { count: 14 }),
    value: 14 * Constants.MSEC_IN_DAY
  },
  { label: I18n.t('numDays', { count: 30 }), value: 30 * Constants.MSEC_IN_DAY }
]

const incidentsCleanupLevelOptions = [
  {
    label: I18n.t('acknowledged'),
    value: Constants.INCIDENT_STATES.ACKNOWLEDGED
  },
  { label: I18n.t('closed'), value: Constants.INCIDENT_STATES.CLOSED }
]

const addMembersAsContactsOptions = [
  { label: I18n.t('no'), value: Constants.ADD_MEMBERS_AS_CONTACTS_OPTIONS.NO },
  {
    label: I18n.t('toAdmins'),
    value: Constants.ADD_MEMBERS_AS_CONTACTS_OPTIONS.ADMINS
  },
  {
    label: I18n.t('toEveryone'),
    value: Constants.ADD_MEMBERS_AS_CONTACTS_OPTIONS.EVERYONE
  }
]

const apiVersionOptions = [
  { label: 'V1', value: 'v1' },
  { label: 'V2', value: 'v2' }
]

const fetchEnterpriseAccount = async ({ queryKey }) => {
  const enterpriseAccountId = queryKey[1]
  const enterpriseAccountDoc = await getDoc(
    doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId)
  )
  return enterpriseAccountDoc.data()
}

const AccountSettings = ({ enterpriseAccountId }) => {
  const queryClient = useQueryClient()
  const { isPending, error, data } = useQuery({
    queryKey: ['enterpriseAccount', enterpriseAccountId],
    queryFn: fetchEnterpriseAccount
  })

  useEffect(() => {
    // Get the data retention duration from Firestore and listen for changes
    const unsubscribe = onSnapshot(
      doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId),
      () =>
        queryClient.invalidateQueries([
          'enterpriseAccount',
          enterpriseAccountId
        ])
    )

    return () => {
      unsubscribe()
    }
  }, [queryClient, enterpriseAccountId])

  const onChangeIncidentsRetentionDuration = duration => {
    updateDoc(
      doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId),
      {
        incidentsRetentionDuration: duration
      }
    )
  }

  const onChangeIncidentsCleanupLevel = level => {
    updateDoc(
      doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId),
      {
        incidentsCleanupLevel: level
      }
    )
  }

  const onChangeAddMembersAsContacts = value => {
    GlobalConfig.showAlert(
      '',
      I18n.t('resyncMembersPrompt'),
      [
        {
          text: I18n.t('no'),
          onPress: () => {
            updateDoc(
              doc(
                GlobalConfig.firestoreDb,
                'enterpriseAccounts',
                enterpriseAccountId
              ),
              {
                addMembersAsContacts: value
              }
            )
          }
        },
        {
          text: I18n.t('yes'),
          onPress: async () => {
            updateDoc(
              doc(
                GlobalConfig.firestoreDb,
                'enterpriseAccounts',
                enterpriseAccountId
              ),
              {
                addMembersAsContacts: value
              }
            )
            await onSyncAllProMembers()
          }
        }
      ],
      false
    )
  }

  const onChangeApiVersion = version => {
    updateDoc(
      doc(GlobalConfig.firestoreDb, 'enterpriseAccounts', enterpriseAccountId),
      {
        apiVersion: version
      }
    )
  }

  const [isSyncing, setIsSyncing] = useState(false)

  const onSyncAllProMembers = async () => {
    try {
      setIsSyncing(true)
      const functions = getFunctions()
      const syncAllProMembers = httpsCallable(functions, 'syncAllProMembers')
      const result = await syncAllProMembers({ enterpriseAccountId })
      console.log('Sync members result:', result.data)
      if (result.data?.status === 'success') {
        GlobalConfig.showTransientAlert({
          message: I18n.t('proMembersSyncCompleted'),
          duration: Constants.AlertDurations.SHORT
        })
      } else {
        GlobalConfig.showTransientAlert({
          message: I18n.t('errorSyncingProMembers', {
            error: result.data?.message || 'Internal error'
          }),
          duration: Constants.AlertDurations.LONG,
          type: 'warning'
        })
      }
    } catch (error) {
      console.error('Error syncing members:', error)
      GlobalConfig.showTransientAlert({
        message: I18n.t('errorSyncingProMembers', { error: error.message }),
        duration: Constants.AlertDurations.LONG,
        type: 'warning'
      })
    } finally {
      setIsSyncing(false)
    }
  }

  if (isPending) {
    return (
      <Box pad="medium" align="center" gap="small">
        <Spinner />
        <Text>{I18n.t('fetchingData')}</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Box pad="medium" align="center" gap="small">
        <Spinner />
        <Text>{I18n.t('errorFetchingData', { message: error.message })}</Text>
      </Box>
    )
  }

  if (data.incidentsRetentionDuration === undefined) {
    data.incidentsRetentionDuration =
      GlobalConfig.defaultIncidentsRetentionDuration
  }

  if (data.incidentsCleanupLevel === undefined) {
    data.incidentsCleanupLevel = GlobalConfig.defaultIncidentsCleanupLevel
  }

  if (data.addMembersAsContacts === undefined) {
    data.addMembersAsContacts = Constants.ADD_MEMBERS_AS_CONTACTS_OPTIONS.ADMINS // Default to admins
  }

  if (data.apiVersion === undefined) {
    data.apiVersion = 'v1'
  }

  return (
    <Box align="center">
      <Heading level={4}>{I18n.t('settings')}</Heading>
      <Box overflow="auto">
        <Box flex={{ shrink: 0 }}>
          <Card
            pad="medium"
            width="large"
            background="white"
            elevation="none"
            gap="small">
            <Text weight={500} margin={'none'}>
              {I18n.t('apiConfiguration')}
            </Text>
            <HorizontalLine height="1px" color="mediumTint" />
            <Box
              direction="row"
              margin="small"
              gap="small"
              align="center"
              justify="between">
              <Box flex>
                <Text level={4}>{I18n.t('apiVersion')}</Text>
                <Text size="small" color="darkTint">
                  {I18n.t('apiVersionDescription')}
                </Text>
              </Box>
              <Select
                width={{ max: '150px' }}
                alignSelf="start"
                options={apiVersionOptions}
                value={data.apiVersion}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  onChangeApiVersion(option.value)
                }}
              />
            </Box>
            <Text weight={500} margin={'none'}>
              {I18n.t('dataRetention')}
            </Text>
            <HorizontalLine height="1px" color="mediumTint" />
            <Box
              direction="row"
              margin="small"
              gap="small"
              align="center"
              justify="between">
              <Box flex>
                <Text level={4}>{I18n.t('retainIncidentsForDuration')}</Text>
                <Text size="small" color="darkTint">
                  {I18n.t('retainIncidentsForDurationDescription')}
                </Text>
              </Box>
              <Select
                width={{ max: '150px' }}
                alignSelf="start"
                options={incidentsRetentionDurationOptions}
                value={data.incidentsRetentionDuration}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  onChangeIncidentsRetentionDuration(option.value)
                }}
              />
            </Box>
            <Box
              direction="row"
              margin="small"
              gap="small"
              align="center"
              justify="between">
              <Box flex>
                <Text level={4}>{I18n.t('incidentsCleanupLevel')}</Text>
                <Text size="small" color="darkTint">
                  {I18n.t('incidentsCleanupLevelDescription')}
                </Text>
              </Box>
              <Select
                width={{ max: '150px' }}
                alignSelf="start"
                options={incidentsCleanupLevelOptions}
                value={data.incidentsCleanupLevel}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  onChangeIncidentsCleanupLevel(option.value)
                }}
              />
            </Box>
          </Card>
          <Card
            pad="medium"
            width="large"
            background="white"
            elevation="none"
            gap="small"
            margin={{ top: 'medium' }}>
            <Text weight={500} margin={'none'}>
              {I18n.t('general')}
            </Text>
            <HorizontalLine height="1px" color="mediumTint" />
            <Box direction="row" margin="small" gap="small" justify="between">
              <Box flex>
                <Text level={4}>{I18n.t('addMembersAsContacts')}</Text>
                <Text size="small" color="darkTint">
                  {I18n.t('addMembersAsContactsDescription')}
                </Text>
              </Box>
              <Select
                width={{ max: '150px' }}
                alignSelf="start"
                options={addMembersAsContactsOptions}
                value={data.addMembersAsContacts}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  onChangeAddMembersAsContacts(option.value)
                }}
              />
              <Refresh
                style={{
                  cursor: isSyncing ? 'wait' : 'pointer',
                  marginTop: 10
                }}
                color={isSyncing ? 'lightTint' : 'brand'}
                onClick={isSyncing ? undefined : onSyncAllProMembers}
              />
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  )
}

export default AccountSettings
